/**
 * AddEpisodeModal — batch episode/chapter uploader (Feb 25 2026).
 *
 * Features
 * --------
 * - Pick or drag-drop multiple audio/video files at once.
 * - Each file gets an editable title (defaults to the file's basename) and a
 *   sequential episode/chapter number starting at the next available slot.
 * - Sequential upload (one at a time) so a slow connection or an oversize
 *   file doesn't choke the server. Per-file progress bar + bps + ETA.
 * - Per-file failure surfaces the backend's `detail` string; other files
 *   keep going. The user can retry just the failed rows.
 * - "Cancel upload" aborts the in-flight XHR and queued items.
 *
 * Hits:
 *   POST /api/cinematic/series/{id}/episode
 *   POST /api/audiobook/series/{id}/chapter
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  X, Upload as UploadIcon, Film, Headphones, Loader2,
  CheckCircle2, AlertCircle, RotateCcw, Trash2, Plus, Calendar, Image as ImageIcon,
  AudioWaveform, FileAudio,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from './ui/button';
import { Input } from './ui/input';
import OriginalContentAck from './OriginalContentAck';
import { recordOriginalAck } from '../utils/originalAck';
import { authFetch } from '../utils/authUtils';
import VoicePreviewButton from './VoicePreviewButton';

const STATUS = {
  PENDING: 'pending',
  UPLOADING: 'uploading',
  DONE: 'done',
  ERROR: 'error',
};

const fmtMb = (b) => `${(b / (1024 * 1024)).toFixed(1)}`;

const AddEpisodeModal = ({ series, existingEpisodes = [], onClose, onUploaded, prefilledFiles = null }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  // `isCinematic` decides which API endpoint we POST to. Older series
  // docs (and any caller that built a series object client-side) may
  // not carry `type` / `content_type`, so we fall back to the
  // series_id prefix convention from the backend:
  //   • cinematic series IDs look like `series_<hex>`
  //   • audiobook series IDs look like `audiobook_series_<hex>`
  // Without this fallback a cinematic series whose `type` field is
  // missing would POST to /api/audiobook/series/.../chapter and 404
  // with "Series not found".
  const isCinematic = (() => {
    const t = (series?.type || series?.content_type || '').toLowerCase();
    if (t === 'cinematic') return true;
    if (t === 'audiobook') return false;
    const sid = String(series?.series_id || series?.book_id || '');
    if (sid.startsWith('audiobook_series_')) return false;
    if (sid.startsWith('series_')) return true;
    return false;
  })();
  const itemLabel = isCinematic ? 'episode' : 'chapter';
  const itemLabelPlural = isCinematic ? 'episodes' : 'chapters';
  const Icon = isCinematic ? Film : Headphones;

  // Admin-only features (scheduled release).
  const isAdmin = (() => {
    try { return localStorage.getItem('userRole') === 'admin'; }
    catch { return false; }
  })();

  const baseNumber =
    (existingEpisodes.reduce(
      (max, ep) =>
        Math.max(max, ep.episode_number || ep.chapter_number || 0),
      0,
    ) || 0) + 1;

  // Each row: { id, file, title, number, status, progress (0-100), bps, error }
  const [queue, setQueue] = useState([]);

  // Mode toggle: "files" (drag-drop pre-recorded audio) vs "tts" (paste
  // text + pick voice → server-side synthesis). Spec Jun 5 2026.
  const [mode, setMode] = useState('files');
  // Available custom voices for the TTS mode picker.
  const [voices, setVoices] = useState(['default']);
  const [voicesDetailed, setVoicesDetailed] = useState([]);
  const [viewerIsPremium, setViewerIsPremium] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/tts/voices`);
        if (!r.ok) return;
        const d = await r.json();
        if (cancelled) return;
        const list = Array.isArray(d.voices) ? d.voices : [];
        const norm = list
          .map((v) => (typeof v === 'string' ? v : (v.id || v.name)))
          .filter(Boolean);
        if (norm.length) setVoices(norm);
        // OpenAI voices arrive in `voices_detailed` with provider + locked
        // flags so the dropdown can show "🔒 Premium" badges and disable
        // the option for free-tier writers (premium can still pick them
        // and route to OpenAI TTS for instant studio narration).
        if (Array.isArray(d.voices_detailed)) setVoicesDetailed(d.voices_detailed);
        setViewerIsPremium(Boolean(d.viewer_is_premium));
      } catch { /* keep ["default"] fallback */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push a blank text-episode row onto the queue (TTS mode).
  const addTtsRow = () => {
    setQueue((curr) => {
      const start = baseNumber + curr.length;
      const number = start;
      return [
        ...curr,
        {
          id: `tts-${Date.now()}-${number}`,
          useTts: true,
          ttsText: '',
          ttsVoice: voices[0] || 'default',
          file: null,
          title: `Episode ${number}: `,
          number: String(number),
          releaseAt: '',
          coverFile: null,
          coverPreview: '',
          status: STATUS.PENDING,
          progress: 0,
          bps: 0,
          error: '',
        },
      ];
    });
  };
  const [busy, setBusy] = useState(false);
  // Originality acknowledgement — applies to the WHOLE batch (covers every
  // file the writer is about to upload in this session).
  const [originalAck, setOriginalAck] = useState(false);
  const xhrRef = useRef(null);
  const cancelledRef = useRef(false);

  const addFiles = (files) => {
    if (!files || !files.length) return;
    const accepted = Array.from(files).filter((f) => {
      if (!isCinematic) return f.type.startsWith('audio/');
      return f.type.startsWith('audio/') || f.type.startsWith('video/');
    });
    if (accepted.length < files.length) {
      toast.error(`${files.length - accepted.length} file(s) skipped — wrong type.`);
    }
    setQueue((curr) => {
      const start = baseNumber + curr.length;
      const next = accepted.map((f, idx) => {
        const number = start + idx;
        const stem = f.name.replace(/\.[^.]+$/, '').trim();
        // Episode title default: "Episode N: <stem>". The "Episode N:" prefix
        // is auto-maintained when the user later edits the number (see
        // QueueRow.handleNumberChange) so the number-in-title always matches.
        return {
          id: `${Date.now()}-${idx}-${f.name}`,
          file: f,
          title: `Episode ${number}: ${stem}`,
          number: String(number),
          releaseAt: '',
          coverFile: null,
          coverPreview: '',
          status: STATUS.PENDING,
          progress: 0,
          bps: 0,
          error: '',
        };
      });
      return [...curr, ...next];
    });
  };

  const updateRow = (id, patch) => {
    setQueue((curr) => curr.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const removeRow = (id) => {
    setQueue((curr) => curr.filter((r) => r.id !== id));
  };

  const handleFileChange = (e) => addFiles(e.target.files);

  // Hydrate the queue from prefilledFiles (passed in by LibraryQuickDrop when
  // the admin drops audio files anywhere on the Library page). Runs once on
  // mount so we don't keep re-adding the same files on re-renders.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    if (prefilledFiles && prefilledFiles.length) {
      hydratedRef.current = true;
      addFiles(prefilledFiles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefilledFiles]);

  // Drag-drop wiring on the dropzone.
  const [dragOver, setDragOver] = useState(false);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer?.files);
  };

  // ────────────────────────────────────────────────────── upload one row
  const uploadRow = (row) =>
    new Promise((resolve) => {
      const seriesId = series.series_id || series.book_id;
      const fd = new FormData();
      // TTS mode: post the script + voice INSTEAD of a file. Backend
      // synthesises the audio and writes the episode row. File mode is
      // unchanged.
      if (row.useTts) {
        if (isCinematic) {
          fd.append('episode_text', row.ttsText || '');
          fd.append('episode_title', row.title || `Episode ${row.number}`);
          fd.append('episode_number', row.number || '1');
        } else {
          fd.append('chapter_text', row.ttsText || '');
          fd.append('chapter_title', row.title || `Chapter ${row.number}`);
          fd.append('chapter_number', row.number || '1');
        }
        fd.append('tts_voice', row.ttsVoice || 'default');
      } else {
        fd.append('file', row.file);
        if (isCinematic) {
          fd.append('episode_title', row.title || `Episode ${row.number}`);
          fd.append('episode_number', row.number || '1');
        } else {
          fd.append('chapter_title', row.title || `Chapter ${row.number}`);
          fd.append('chapter_number', row.number || '1');
        }
      }
      // Admin-only: schedule a release. Browser datetime-local is naive
      // (no timezone); we serialise as local ISO and let the backend
      // promote it to UTC.
      if (row.releaseAt) {
        try {
          const d = new Date(row.releaseAt);
          if (!isNaN(d.getTime())) fd.append('release_at', d.toISOString());
        } catch { /* noop */ }
      }
      // Optional per-row cover image — backend defaults to series cover when omitted.
      if (row.coverFile) {
        fd.append('cover_image', row.coverFile);
      }

      const endpoint = isCinematic
        ? `/api/cinematic/series/${seriesId}/episode`
        : `/api/audiobook/series/${seriesId}/chapter`;

      const xhr = new XMLHttpRequest();
      xhrRef.current = xhr;
      xhr.open('POST', `${BACKEND_URL}${endpoint}`);
      xhr.withCredentials = true;
      try {
        const tok = localStorage.getItem('access_token');
        if (tok) xhr.setRequestHeader('Authorization', `Bearer ${tok}`);
      } catch { /* noop */ }

      let lastTick = Date.now();
      let lastBytes = 0;
      xhr.upload.addEventListener('progress', (ev) => {
        if (!ev.lengthComputable) return;
        const now = Date.now();
        const dt = (now - lastTick) / 1000;
        let nextBps;
        if (dt >= 0.4) {
          nextBps = (ev.loaded - lastBytes) / Math.max(dt, 0.001);
          lastTick = now;
          lastBytes = ev.loaded;
        }
        updateRow(row.id, {
          status: STATUS.UPLOADING,
          progress: Math.round((ev.loaded / ev.total) * 100),
          bps: nextBps !== undefined ? nextBps : row.bps,
          sentBytes: ev.loaded,
          totalBytes: ev.total,
        });
      });

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          // Pluck out the new id and stamp the originality acknowledgement
          // onto it before resolving so analytics/admin tooling can see
          // exactly when the writer agreed.
          try {
            const j = JSON.parse(xhr.responseText || '{}');
            const newId = j.chapter_id || j.episode_id || j.book_id || j.content_id;
            if (newId) recordOriginalAck(newId).catch(() => {});
          } catch { /* not JSON, skip */ }
          updateRow(row.id, { status: STATUS.DONE, progress: 100, bps: 0 });
          resolve(true);
          return;
        }
        let detail = `${xhr.status} ${xhr.statusText}`;
        try {
          const j = JSON.parse(xhr.responseText || '{}');
          if (typeof j.detail === 'string') detail = j.detail;
          else if (j.detail) detail = JSON.stringify(j.detail);
          else if (j.message) detail = j.message;
        } catch { /* not JSON */ }
        if (xhr.status === 401) detail = 'Session expired';
        else if (xhr.status === 403) detail = "You don't have permission";
        else if (xhr.status === 404) detail = 'Series not found';
        else if (xhr.status === 413) detail = 'File too large';
        updateRow(row.id, { status: STATUS.ERROR, error: detail });
        resolve(false);
      };
      xhr.onerror = () => {
        updateRow(row.id, { status: STATUS.ERROR, error: 'Network error' });
        resolve(false);
      };
      xhr.onabort = () => {
        updateRow(row.id, { status: STATUS.ERROR, error: 'Cancelled' });
        resolve(false);
      };
      xhr.send(fd);
    });

  // ────────────────────────────────────────────────────── upload queue
  const startBatch = async () => {
    if (busy) return;
    if (!originalAck) {
      toast.error('Please confirm the originality acknowledgement first.');
      return;
    }
    const pending = queue.filter((r) => r.status === STATUS.PENDING || r.status === STATUS.ERROR);
    if (!pending.length) {
      toast.error('Nothing to upload — pick at least one file.');
      return;
    }
    // TTS rows need a script. Block the batch with a clear error rather
    // than silently posting empty text and getting "text is empty" back
    // from the server.
    const emptyTts = pending.find((r) => r.useTts && !(r.ttsText || '').trim());
    if (emptyTts) {
      toast.error(`Episode ${emptyTts.number}: add the script before generating.`);
      return;
    }
    setBusy(true);
    cancelledRef.current = false;
    let okCount = 0;
    let failCount = 0;
    for (const row of pending) {
      if (cancelledRef.current) break;
      // Reset row error before retry.
      updateRow(row.id, { status: STATUS.UPLOADING, progress: 0, error: '' });
      const ok = await uploadRow(row);
      if (ok) okCount += 1;
      else failCount += 1;
    }
    xhrRef.current = null;
    setBusy(false);
    if (okCount > 0) {
      toast.success(
        okCount === 1
          ? `1 ${itemLabel} added!`
          : `${okCount} ${itemLabelPlural} added!`,
      );
      onUploaded?.();
    }
    if (failCount > 0 && !cancelledRef.current) {
      toast.error(`${failCount} ${failCount === 1 ? 'failed' : 'failed'} — check the list to retry.`);
    }
    // If everything succeeded → close after a beat.
    if (okCount > 0 && failCount === 0) {
      setTimeout(onClose, 700);
    }
  };

  const cancelBatch = () => {
    cancelledRef.current = true;
    if (xhrRef.current) {
      try { xhrRef.current.abort(); } catch { /* noop */ }
    }
  };

  useEffect(() => () => {
    if (xhrRef.current) {
      try { xhrRef.current.abort(); } catch { /* noop */ }
    }
  }, []);

  const totalCount = queue.length;
  const doneCount = queue.filter((r) => r.status === STATUS.DONE).length;
  const errCount = queue.filter((r) => r.status === STATUS.ERROR).length;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={busy ? undefined : onClose}
      data-testid="add-episode-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-slate-900 border border-indigo-500/40 rounded-2xl shadow-[0_0_60px_-15px_rgba(99,102,241,0.45)] overflow-hidden flex flex-col max-h-[90vh]"
      >
        <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-700">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-indigo-600/30 border border-indigo-400/40 flex items-center justify-center shrink-0">
              <Icon className="w-5 h-5 text-indigo-200" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-bold text-white">
                Add {itemLabelPlural}
              </h3>
              <p className="text-xs text-gray-400 truncate">
                to <span className="text-indigo-300 font-semibold">{series?.title}</span>
                {totalCount > 0 && (
                  <span className="ml-2 text-gray-500">
                    · {doneCount}/{totalCount} done
                    {errCount > 0 && <span className="text-red-400"> · {errCount} failed</span>}
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={busy ? undefined : onClose}
            disabled={busy}
            className="p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-slate-800 disabled:opacity-50"
            aria-label="Close"
            data-testid="add-episode-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Mode toggle — Files vs Generate from text (TTS).
            Jun 5 2026 spec: writers/admins can compose an episode by
            pasting text + picking a custom voice instead of uploading. */}
        <div className="mx-5 mt-4 flex items-center gap-1.5 bg-slate-800/50 border border-slate-700 rounded-xl p-1">
          {[
            { id: 'files', label: 'Upload files', icon: FileAudio },
            { id: 'tts',   label: 'Generate from text', icon: AudioWaveform },
          ].map((m) => {
            const M = m.icon;
            const active = mode === m.id;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => setMode(m.id)}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 ${
                  active
                    ? 'bg-indigo-500/30 text-white border border-indigo-300/60 shadow-[0_0_0_1px_rgba(99,102,241,0.25)]'
                    : 'text-slate-300 border border-transparent hover:bg-slate-800'
                }`}
                data-testid={`add-episode-mode-${m.id}`}
              >
                <M className="w-4 h-4" />
                {m.label}
              </button>
            );
          })}
        </div>

        {/* File picker — only in files mode */}
        {mode === 'files' ? (
        <div
          onDrop={onDrop}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          className={`mx-5 mt-4 rounded-xl border-2 border-dashed transition-all ${
            dragOver
              ? 'border-indigo-400 bg-indigo-500/10'
              : 'border-slate-600 bg-slate-800/30 hover:border-indigo-500/60'
          }`}
        >
          <label
            htmlFor="add-ep-file"
            className="flex flex-col items-center justify-center gap-1.5 px-4 py-5 cursor-pointer"
            data-testid="add-episode-dropzone"
          >
            <UploadIcon className="w-5 h-5 text-indigo-300" />
            <p className="text-sm text-white">
              Drag-and-drop {itemLabelPlural} here, or <span className="text-indigo-300 font-semibold">browse</span>
            </p>
            <p className="text-[10px] text-gray-500">
              {isCinematic ? 'mp4 / webm / mp3' : 'mp3 / wav / m4a'} · multi-select supported · auto-numbered from #{baseNumber}
            </p>
            <input
              id="add-ep-file"
              type="file"
              multiple
              accept={isCinematic ? 'audio/*,video/*' : 'audio/*'}
              onChange={handleFileChange}
              className="hidden"
              data-testid="add-episode-file"
            />
          </label>
        </div>
        ) : (
          /* TTS mode add-button */
          <div className="mx-5 mt-4 rounded-xl border-2 border-dashed border-fuchsia-500/40 bg-fuchsia-500/[0.04] p-4 text-center">
            <p className="text-sm text-white inline-flex items-center gap-2 justify-center mb-1.5">
              <AudioWaveform className="w-4 h-4 text-fuchsia-300" />
              Generate {itemLabelPlural} from text
            </p>
            <p className="text-[10px] text-gray-400 mb-2.5">
              Paste your script, pick a voice — the server synthesises the audio.
              Each row becomes one {itemLabel}.
            </p>
            <button
              type="button"
              onClick={addTtsRow}
              className="inline-flex items-center gap-1.5 rounded-md bg-fuchsia-600 hover:bg-fuchsia-500 text-white text-xs font-bold px-3 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-300"
              data-testid="add-episode-add-tts-row"
            >
              <Plus className="w-3.5 h-3.5" /> Add text {itemLabel}
            </button>
          </div>
        )}

        {/* Queue list */}
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
          {queue.length === 0 ? (
            <p className="text-xs text-gray-500 text-center py-6">
              No files yet. Pick or drop one or many to start.
            </p>
          ) : (
            queue.map((row) => <QueueRow
              key={row.id}
              row={row}
              busy={busy}
              isCinematic={isCinematic}
              isAdmin={isAdmin}
              voices={voices}
              voicesDetailed={voicesDetailed}
              viewerIsPremium={viewerIsPremium}
              onChange={(patch) => updateRow(row.id, patch)}
              onRemove={() => removeRow(row.id)}
            />)
          )}
        </div>

        {/* Originality acknowledgement — required before any file in the
            batch can be sent. Hidden once the upload is in flight so the
            footer's Cancel button has room. */}
        {!busy && queue.length > 0 && (
          <div className="px-5 pb-3">
            <OriginalContentAck
              checked={originalAck}
              onChange={setOriginalAck}
              testId="add-episode-original-ack"
            />
          </div>
        )}

        {/* Footer */}
        <div className="px-5 py-4 border-t border-slate-700 bg-slate-900/60 flex gap-2 shrink-0">
          {!busy ? (
            <>
              <label
                htmlFor="add-ep-file-2"
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm bg-slate-800 hover:bg-slate-700 border border-slate-700 text-indigo-200 cursor-pointer"
              >
                <Plus className="w-4 h-4" /> Add more
              </label>
              <input
                id="add-ep-file-2"
                type="file"
                multiple
                accept={isCinematic ? 'audio/*,video/*' : 'audio/*'}
                onChange={handleFileChange}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="text-gray-300 border-slate-600"
                data-testid="add-episode-cancel"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={startBatch}
                disabled={queue.length === 0 || !originalAck}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="add-episode-submit"
              >
                <UploadIcon className="w-4 h-4 mr-2" />
                {errCount > 0
                  ? `Retry failed (${errCount})`
                  : queue.length > 1
                  ? `Upload ${queue.filter((r) => r.status !== STATUS.DONE).length} ${itemLabelPlural}`
                  : `Upload`}
              </Button>
            </>
          ) : (
            <Button
              type="button"
              onClick={cancelBatch}
              variant="outline"
              className="flex-1 text-red-300 border-red-500/40 hover:bg-red-500/10"
              data-testid="add-episode-abort"
            >
              <X className="w-4 h-4 mr-1" /> Cancel upload
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const QueueRow = ({ row, busy, isCinematic, isAdmin, voices = ['default'], voicesDetailed = [], viewerIsPremium = false, onChange, onRemove }) => {
  const Icon = isCinematic ? Film : Headphones;
  const isDone = row.status === STATUS.DONE;
  const isErr = row.status === STATUS.ERROR;
  const isUploading = row.status === STATUS.UPLOADING;
  const scheduledForFuture = (() => {
    if (!row.releaseAt) return false;
    const d = new Date(row.releaseAt);
    return !isNaN(d.getTime()) && d.getTime() > Date.now();
  })();

  return (
    <div
      className={`rounded-lg border p-3 transition-colors ${
        isDone
          ? 'border-emerald-500/30 bg-emerald-500/5'
          : isErr
          ? 'border-red-500/30 bg-red-500/5'
          : 'border-slate-700 bg-slate-800/40'
      }`}
      data-testid={`queue-row-${row.id}`}
    >
      <div className="flex items-start gap-2">
        <div className="w-8 h-8 rounded-md bg-indigo-600/20 border border-indigo-500/30 flex items-center justify-center shrink-0 mt-0.5">
          {isDone ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          ) : isErr ? (
            <AlertCircle className="w-4 h-4 text-red-400" />
          ) : isUploading ? (
            <Loader2 className="w-4 h-4 text-indigo-300 animate-spin" />
          ) : (
            <Icon className="w-4 h-4 text-indigo-300" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex gap-2">
            <Input
              type="number"
              min="1"
              value={row.number}
              disabled={busy || isDone}
              onChange={(e) => {
                const nextNum = e.target.value;
                // Keep the "Episode N:" prefix in the title in sync with the
                // number. We only auto-rewrite when the existing title still
                // starts with the canonical pattern — if the user has fully
                // rewritten the title, we leave it alone.
                const prefixRe = /^Episode\s+\d+\s*:\s*/i;
                if (prefixRe.test(row.title || '')) {
                  const stem = (row.title || '').replace(prefixRe, '');
                  onChange({ number: nextNum, title: `Episode ${nextNum}: ${stem}` });
                } else {
                  onChange({ number: nextNum });
                }
              }}
              className="w-16 h-8 bg-slate-900 border-slate-700 text-white text-xs"
              data-testid={`queue-row-number-${row.id}`}
            />
            <Input
              value={row.title}
              disabled={busy || isDone}
              onChange={(e) => onChange({ title: e.target.value })}
              className="flex-1 h-8 bg-slate-900 border-slate-700 text-white text-xs"
              placeholder="Title"
              data-testid={`queue-row-title-${row.id}`}
            />
            {!busy && !isDone && (
              <button
                type="button"
                onClick={onRemove}
                className="p-1 text-gray-500 hover:text-red-400"
                aria-label="Remove"
                data-testid={`queue-row-remove-${row.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
          <p className="text-[10px] text-gray-500 mt-1 truncate">
            {row.useTts
              ? <><AudioWaveform className="w-3 h-3 inline -mt-0.5 mr-1 text-fuchsia-300" />Text → {row.ttsVoice || 'default'}{row.ttsText ? ` · ${row.ttsText.trim().split(/\s+/).length} words` : ''}</>
              : row.file
                ? <>{row.file.name} · {fmtMb(row.file.size)} MB</>
                : '—'}
            {scheduledForFuture && (
              <span className="ml-2 inline-flex items-center gap-1 text-fuchsia-300">
                <Calendar className="w-3 h-3" />
                Scheduled · {new Date(row.releaseAt).toLocaleString()}
              </span>
            )}
          </p>

          {/* TTS-row editor: script textarea + voice picker */}
          {row.useTts && !isDone && !isUploading && (
            <div className="mt-3 rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/5 p-2.5 space-y-2" data-testid={`queue-row-tts-${row.id}`}>
              <textarea
                value={row.ttsText}
                onChange={(e) => onChange({ ttsText: e.target.value })}
                disabled={busy}
                rows={6}
                placeholder={`Paste the ${isCinematic ? 'episode' : 'chapter'} script here…`}
                className="w-full rounded-md bg-slate-900 border border-slate-700 text-white text-xs px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-fuchsia-500/40 resize-y"
                data-testid={`queue-row-tts-text-${row.id}`}
              />
              {(() => {
                const t = (row.ttsText || '').trim();
                const w = t ? t.split(/\s+/).length : 0;
                return (
                  <p
                    className="text-[10px] text-fuchsia-200/80 -mt-1"
                    data-testid={`queue-row-tts-word-count-${row.id}`}
                  >
                    {w.toLocaleString()} word{w === 1 ? '' : 's'} · {(row.ttsText || '').length.toLocaleString()} characters
                  </p>
                );
              })()}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-widest font-bold text-fuchsia-200">Voice</span>
                {voices.map((v) => {
                  const active = (row.ttsVoice || 'default') === v;
                  // Provider + lock decoration comes from the backend's
                  // `voices_detailed` array (OpenAI voices are flagged
                  // `tier_required="premium"` and `locked=true` for
                  // free writers).
                  const detail = voicesDetailed.find((d) => (d.id || d.name) === v) || null;
                  const isOpenAI = detail?.provider === 'openai';
                  const locked = Boolean(detail?.locked);
                  return (
                    <div
                      key={v}
                      className={`inline-flex items-center gap-1.5 rounded-full border transition ${
                        active
                          ? 'bg-fuchsia-500/25 border-fuchsia-300'
                          : locked
                            ? 'bg-slate-900/60 border-amber-500/40 opacity-70'
                            : 'bg-slate-800 border-slate-700 hover:border-fuchsia-400/60'
                      }`}
                      data-testid={`queue-row-tts-voice-wrap-${row.id}-${v.replace(/\s+/g, '-')}`}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          if (locked) {
                            // Bounce free writers into the Premium upsell.
                            window.dispatchEvent(new CustomEvent('open-premium-modal', {
                              detail: { source: 'voice-lock', voice: v },
                            }));
                            return;
                          }
                          onChange({ ttsVoice: v });
                        }}
                        disabled={busy}
                        title={locked ? `${v} — Premium voice (OpenAI HD). Upgrade to unlock.` : undefined}
                        className={`inline-flex items-center text-[11px] font-bold pl-2.5 pr-1 py-1 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-300 ${
                          active ? 'text-white' : locked ? 'text-amber-200' : 'text-slate-300'
                        }`}
                        data-testid={`queue-row-tts-voice-${row.id}-${v.replace(/\s+/g, '-')}`}
                      >
                        {active && <CheckCircle2 className="w-3 h-3 mr-1 text-fuchsia-200" />}
                        {isOpenAI && !active && (
                          <span className="text-[9px] mr-1 text-emerald-300" aria-hidden>★</span>
                        )}
                        <span className="truncate max-w-[140px] capitalize">{v}</span>
                        {locked && (
                          <span
                            className="ml-1 px-1.5 py-px text-[8px] rounded-full bg-amber-500/20 text-amber-200 border border-amber-400/40 uppercase tracking-wider"
                            data-testid={`queue-row-tts-voice-lock-${row.id}-${v.replace(/\s+/g, '-')}`}
                          >
                            Premium
                          </span>
                        )}
                      </button>
                      {!locked && <VoicePreviewButton voice={v} size="xs" className="mr-1" />}
                    </div>
                  );
                })}
                {!viewerIsPremium && voicesDetailed.some((d) => d.provider === 'openai') && (
                  <span className="text-[9px] text-amber-200/80 ml-1" data-testid="voice-premium-hint">
                    ★ = HD voice · upgrade to use
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Per-row optional cover image picker — explicitly labelled
              as a per-episode cover so admins know it's separate from the
              folder/series cover (which is set on the series itself and
              auto-inherited when this is empty). */}
          {!isDone && !isUploading && (
            <div className="mt-3 rounded-lg border border-indigo-500/30 bg-indigo-500/5 p-2.5">
              <p className="text-[10px] uppercase tracking-wider font-bold text-indigo-300 mb-1.5 flex items-center gap-1">
                <ImageIcon className="w-3 h-3" />
                Episode cover · optional
              </p>
              <div className="flex items-center gap-3">
                {row.coverPreview ? (
                  <img
                    src={row.coverPreview}
                    alt=""
                    className="w-14 h-14 rounded-md object-cover border border-indigo-500/40 shadow"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-md bg-slate-900 border border-dashed border-indigo-500/40 flex items-center justify-center">
                    <ImageIcon className="w-5 h-5 text-indigo-300/60" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <label
                    htmlFor={`cover-${row.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-indigo-600/80 hover:bg-indigo-500 px-3 py-1.5 rounded-md cursor-pointer transition-colors"
                    data-testid={`queue-row-cover-${row.id}`}
                  >
                    <ImageIcon className="w-3.5 h-3.5" />
                    {row.coverFile ? 'Replace cover' : 'Upload cover'}
                  </label>
                  <input
                    id={`cover-${row.id}`}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    disabled={busy}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      const reader = new FileReader();
                      reader.onload = () => {
                        onChange({ coverFile: f, coverPreview: reader.result });
                      };
                      reader.readAsDataURL(f);
                    }}
                    className="hidden"
                  />
                  {row.coverFile ? (
                    <button
                      type="button"
                      onClick={() => onChange({ coverFile: null, coverPreview: '' })}
                      className="ml-2 text-[10px] text-gray-400 hover:text-rose-300 underline underline-offset-2"
                      data-testid={`queue-row-cover-clear-${row.id}`}
                    >
                      Use folder cover instead
                    </button>
                  ) : (
                    <p className="text-[10px] text-gray-400 mt-1 leading-tight">
                      Leave empty to inherit the series/folder cover.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {isAdmin && !isDone && !isUploading && (
            <div className="mt-2 flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5 text-fuchsia-300 shrink-0" />
              <Input
                type="datetime-local"
                value={row.releaseAt}
                disabled={busy}
                onChange={(e) => onChange({ releaseAt: e.target.value })}
                className="h-7 bg-slate-900 border-slate-700 text-white text-[11px] px-2 flex-1"
                data-testid={`queue-row-release-${row.id}`}
                title="Optional — schedule when this episode goes live + push fires (admin-only)"
              />
              {row.releaseAt && (
                <button
                  type="button"
                  onClick={() => onChange({ releaseAt: '' })}
                  className="text-[10px] text-gray-500 hover:text-white"
                  data-testid={`queue-row-release-clear-${row.id}`}
                >
                  Clear
                </button>
              )}
            </div>
          )}

          {(isUploading || isDone) && (
            <>
              <div className="mt-2 flex items-center justify-between gap-2">
                <div className="flex-1 h-1.5 bg-slate-900 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-200 ${
                      isDone
                        ? 'bg-emerald-500'
                        : 'bg-gradient-to-r from-indigo-500 to-fuchsia-500'
                    }`}
                    style={{ width: `${row.progress}%` }}
                  />
                </div>
                <span className="text-[10px] font-mono text-gray-400 shrink-0">
                  {isDone ? 'Done' : `${row.progress}%`}
                  {isUploading && row.bps > 0 && (
                    <span className="ml-1 text-indigo-300">
                      · {(row.bps / (1024 * 1024)).toFixed(2)} MB/s
                    </span>
                  )}
                </span>
              </div>
              {isUploading && row.bps > 0 && row.totalBytes && row.progress < 100 && (
                <p className="text-[10px] text-gray-500 mt-1">
                  ~{Math.max(1, Math.round(
                    (row.totalBytes - row.sentBytes) / Math.max(row.bps, 1)
                  ))}s remaining
                </p>
              )}
            </>
          )}

          {isErr && (
            <div className="mt-2 flex items-center gap-2">
              <p className="text-[11px] text-red-300 flex-1 leading-snug">
                {row.error || 'Upload failed.'}
              </p>
              {!busy && (
                <span className="text-[10px] text-indigo-300 inline-flex items-center gap-1">
                  <RotateCcw className="w-3 h-3" /> Retry from footer
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddEpisodeModal;
